import { Delete, Person, School, Phone, AddCircle } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Divider,
  FormHelperText,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLegajosContext } from "../../../../context/LegajoTabsContext";
import { fetchParentescos, fetchTypesDoc, deleteFamiliar, fetchEstudiosAcademicos } from "../../services/empleados.api";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import moment from "moment";

const PLACEHOLDERS_DOCUMENTOS = {
  DNI: "12345678",
  Pasaporte: "ABC123456",
  "Libreta Civica": "12.345.678",
};

export default function FamiliaresForm({ isEditing, cuil }) {
  const { 
    formData, 
    addFamilyMember, 
    updateFamilyMember, 
    removeFamilyMember, 
    errors 
  } = useLegajosContext();
  
  const [parentescos, setParentescos] = useState([]);
  const [tiposDoc, setTiposDoc] = useState([]);
  const [estudiosAcademicos, setEstudiosAcademicos] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    index: null,
    familiar: null,
  });

  useEffect(() => {
    const cargarDatosIniciales = async () => {
      try {
        const [ParentData, DocData, EstudiosData] = await Promise.all([
          fetchParentescos(),
          fetchTypesDoc(),
          fetchEstudiosAcademicos(),
        ]);
        setParentescos(ParentData);
        setTiposDoc(DocData);
        setEstudiosAcademicos(EstudiosData);
      } catch (error) {
        console.error("Error Al cargar los datos iniciales");
      }
    };
    cargarDatosIniciales();
  }, []);
  const handleDeleteClick = (index, familiar) => {
    setDeleteDialog({ open: true, index, familiar });
  };

  const handleConfirmDelete = async () => {
    if (deleteDialog.index !== null) {
      const { familiar, index } = deleteDialog;
      const cuilEmpleadoEdit = cuil; // CUIL del empleado que se está editando (si es edicion)

      // Si hay CUIL (estamos editando) y el familiar tiene ID (ya existe en DB)
      if (cuilEmpleadoEdit && familiar?.id) {
        try {
          await deleteFamiliar(cuilEmpleadoEdit, familiar.id);
          // Opcionalmente podrías mostrar un toast aquí si tuvieras acceso
        } catch (error) {
          console.error("Error al eliminar familiar de la API:", error);
          // Podrías manejar el error aquí (ej: no eliminar del array si falla la API)
        }
      }

      removeFamilyMember(index); // Elimina el familiar del array local 
    }
    setDeleteDialog({ open: false, index: null, familiar: null });
  };

  const getTipoDocLabel = (id) => {
    return tiposDoc.find(t => t.id === id)?.tipo || "DNI";
  };

  const familiares = formData?.familiares || [];

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Familiares</Typography>
        <Button
          variant="contained"
          startIcon={<AddCircle />}
          onClick={addFamilyMember}
          disabled={isEditing}
          size="small"
        >
          Agregar Familiar
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {familiares.length === 0 ? (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: "center",
                color: "text.secondary",
                bgcolor: "rgba(0,0,0,0.02)",
                borderRadius: 2,
                border: "1px dashed rgba(0,0,0,0.1)",
              }}
            >
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                No hay familiares registrados aún. Haga clic en "Agregar Familiar" para comenzar.
              </Typography>
            </Paper>
          </Grid>
        ) : (
          familiares.map((familiar, index) => (
            <Grid item xs={12} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 2,
                  position: "relative",
                  bgcolor: "rgba(0,0,0,0.01)",
                }}
              >
               

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label="Nombre"
                      value={familiar.nombre || ""}
                      onChange={(e) => updateFamilyMember(index, "nombre", e.target.value)}
                      error={!!errors?.familiares?.[`nombre_${index}`]}
                      helperText={errors?.familiares?.[`nombre_${index}`]}
                      inputProps={{ readOnly: isEditing }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label="Apellido"
                      value={familiar.apellido || ""}
                      onChange={(e) => updateFamilyMember(index, "apellido", e.target.value)}
                      error={!!errors?.familiares?.[`apellido_${index}`]}
                      helperText={errors?.familiares?.[`apellido_${index}`]}
                      inputProps={{ readOnly: isEditing }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <FormControl
                      fullWidth
                      variant="standard"
                      error={!!errors?.familiares?.[`parentesco_id_${index}`]}
                      disabled={isEditing}
                    >
                      <InputLabel>Parentesco</InputLabel>
                      <Select
                        value={familiar.parentesco_id || ""}
                        onChange={(e) => updateFamilyMember(index, "parentesco_id", e.target.value)}
                      >
                        {parentescos.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors?.familiares?.[`parentesco_id_${index}`] && (
                        <FormHelperText>{errors.familiares[`parentesco_id_${index}`]}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <FormControl
                      fullWidth
                      variant="standard"
                      error={!!errors?.familiares?.[`id_tipo_doc_${index}`]}
                      disabled={isEditing}
                    >
                      <InputLabel>Tipo de Doc</InputLabel>
                      <Select
                        value={familiar.id_tipo_doc || ""}
                        onChange={(e) => {
                          updateFamilyMember(index, "id_tipo_doc", e.target.value);
                          updateFamilyMember(index, "nro_doc", "");
                        }}
                      >
                        {tiposDoc.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.tipo}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors?.familiares?.[`id_tipo_doc_${index}`] && (
                        <FormHelperText>{errors.familiares[`id_tipo_doc_${index}`]}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={getTipoDocLabel(familiar.id_tipo_doc)}
                      placeholder={PLACEHOLDERS_DOCUMENTOS[getTipoDocLabel(familiar.id_tipo_doc)]}
                      value={familiar.nro_doc || ""}
                      onChange={(e) => updateFamilyMember(index, "nro_doc", e.target.value)}
                      error={!!errors?.familiares?.[`nro_doc_${index}`]}
                      helperText={errors?.familiares?.[`nro_doc_${index}`]}
                      inputProps={{ readOnly: isEditing }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label="Teléfono"
                      value={familiar.telefono || ""}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        updateFamilyMember(index, "telefono", val);
                      }}
                      error={!!errors?.familiares?.[`telefono_${index}`]}
                      helperText={
                        errors?.familiares?.[`telefono_${index}`] || 
                        (familiar.telefono && familiar.telefono.length < 10 ? `Faltan ${10 - familiar.telefono.length} dígitos` : "")
                      }
                      inputProps={{ readOnly: isEditing }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label="Fecha Nacimiento"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={moment(familiar.fecha_nacimiento).format("YYYY-MM-DD")}
                      onChange={(e) => updateFamilyMember(index, "fecha_nacimiento", moment(e.target.value).format("YYYY-MM-DD"))}
                      error={!!errors?.familiares?.[`fecha_nacimiento_${index}`]}
                      helperText={errors?.familiares?.[`fecha_nacimiento_${index}`]}
                      inputProps={{ readOnly: isEditing }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label="Domicilio"
                      value={familiar.domicilio || ""}
                      onChange={(e) => updateFamilyMember(index, "domicilio", e.target.value)}
                      inputProps={{ readOnly: isEditing }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <FormControl fullWidth variant="standard">
                      <InputLabel>Estudio Académico</InputLabel>
                      <Select
                        value={familiar.estudio_id || ""}
                        onChange={(e) => updateFamilyMember(index, "estudio_id", e.target.value)}
                        error={!!errors?.familiares?.[`estudio_id_${index}`]}
                        inputProps={{ readOnly: isEditing }}
                      >
                        <MenuItem value="" disabled>Seleccionar Estudio...</MenuItem>
                        {estudiosAcademicos.map((estudio) => (
                          <MenuItem key={estudio.id} value={estudio.id}>
                            {estudio.estudio}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors?.familiares?.[`estudio_id_${index}`]}</FormHelperText>
                    </FormControl>
                  </Grid>
                           <Grid item xs={12} sm={6} md={2} lg={3}>
                  {!isEditing && (
                    <IconButton
                      onClick={() => handleDeleteClick(index, familiar)}
                      color="error"
                      size="large"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
                </Grid>
       
              </Paper>
             
            </Grid>
          ))
        )}
      </Grid>

      <ConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, index: null, familiar: null })}
        onConfirm={handleConfirmDelete}
        title="Eliminar familiar"
        message={
          deleteDialog.familiar
            ? `¿Estás seguro de que querés eliminar a ${deleteDialog.familiar.nombre} ${deleteDialog.familiar.apellido} de la lista?`
            : "¿Estás seguro de que querés eliminar este familiar de la lista?"
        }
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
        variant="danger"
      />
    </Box>
  );
}
